import React, { useState, useImperativeHandle } from 'react';
import { StaffPerformance, StaffMonthlyTrendStats } from '../types/SupportTypes';
import { calculateSupportMemberPerformance } from '../utils/supportPerformanceUtils';
import MetricRow from './MetricRow';
import '../styles/supporttrumpcard.css';
import { BarChart } from '@mui/icons-material';

interface SupportMemberTrumpCardProps {
    member: StaffPerformance | string;
    monthlyTrendStats: StaffMonthlyTrendStats;
    active?: boolean;
    onClick: () => void;
    onCloseActive?: () => void;
    cardRef?: React.RefObject<{ flipToFront: () => boolean } | null>;
    isMobile?: boolean;
    onMobileDataClick?: () => void;
}

// Support Member Trump Card Component
const SupportMemberTrumpCard: React.FC<SupportMemberTrumpCardProps> = ({
    member,
    monthlyTrendStats,
    active = false,
    onClick,
    onCloseActive,
    cardRef,
    isMobile = false,
    onMobileDataClick
}) => {
    const [isFlipped, setIsFlipped] = useState(false);
    
    // Extract member name to handle both string and StaffPerformance types
    const memberName = typeof member === 'string' ? member : member.name;

    // Expose the flipToFront method through the ref
    useImperativeHandle(cardRef, () => ({
        flipToFront: () => {
            if (isFlipped) {
                setIsFlipped(false);
                return true;
            }
            return false;
        }
    }));

    // Use the centralized utility function to calculate performance metrics
    const metrics = calculateSupportMemberPerformance(member, monthlyTrendStats);

    // Determine card styling based on performance

    const topPerformerScore = 70;
    const bottomPerformerScore = 30;

    const cardColor = metrics.overallScore >= topPerformerScore ? '#e8f5e9' : metrics.overallScore < bottomPerformerScore ? '#ffebee' : '#fff781';
    const borderColor = metrics.overallScore >= topPerformerScore ? '#4caf50' : metrics.overallScore < bottomPerformerScore ? '#f44336' : '#9e9e9e';
    const headerColor = metrics.overallScore >= topPerformerScore ? '#2e7d32' : metrics.overallScore < bottomPerformerScore ? '#c62828' : '#b89900';

    // Handle card click to flip
    const handleCardClick = (e: React.MouseEvent) => {
        if (active) {
            setIsFlipped(!isFlipped);
        }
    };

    // Format time for display
    const formatTime = (minutes: number): string => {
        if (minutes < 60) {
            return `${minutes.toFixed(0)}m`;
        } else if (minutes < 1440) {
            const hours = Math.floor(minutes / 60);
            const remainingMinutes = Math.floor(minutes % 60);
            return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
        } else {
            const days = Math.floor(minutes / 1440);
            const remainingMinutes = minutes % 1440;
            const hours = Math.floor(remainingMinutes / 60);
            if (hours > 0) {
                return `${days}d ${hours}h`;
            } else {
                return `${days}d`;
            }
        }
    };

    return (
        <>
            {/* <style>{cardFlipStyles}</style> */}
            <div className={`support-trump-card-wrapper ${active ? 'active' : ''}`} style={{
                transform: active ? 'none' : undefined,
                transition: 'transform 0.2s ease-out'
            }} onMouseEnter={(e) => {
                if (!active) {
                    e.currentTarget.style.transformOrigin = 'bottom left';
                    e.currentTarget.style.transform = 'scale(1.05)';
                }
            }} onMouseLeave={(e) => {
                if (!active) {
                    e.currentTarget.style.transformOrigin = 'bottom left';
                    e.currentTarget.style.transform = 'scale(1)';
                }
            }}>
                <div className="support-trump-card-container" onClick={handleCardClick}>
                    <div className={`support-trump-card-inner ${isFlipped ? 'flipped' : ''}`}>
                        {/* Front of card */}
                        <div className="support-trump-card-front"
                            style={{
                                background: cardColor,
                                border: `2px solid ${borderColor}`
                            }}
                        >
                            {/* Card Header */}
                            <div className="support-trump-card-header" style={{ background: headerColor }}>
                                <div className="support-trump-card-header-content">
                                    <h3 className="support-trump-card-header-title">{memberName}</h3>
                                    {metrics.isNewStaff ? (
                                        <div className="support-trump-card-new-badge" style={{ color: headerColor }}>NEW</div>
                                    ) : (
                                        <div className="support-trump-card-placeholder"></div>
                                    )}
                                </div>
                                {!active ? (
                                    <span className="support-trump-card-productivity">
                                        {metrics.overallScore.toFixed(1)}%
                                    </span>
                                ) : active && onCloseActive ? (
                                    <>
                                        {isMobile && (
                                            <button
                                                className="support-trump-card-close-button"
                                                style={{ right: '40px' }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onMobileDataClick?.();
                                                }}
                                                aria-label="Open data view"
                                                title="Open data view"
                                            >
                                                <BarChart sx={{ fontSize: 18 }} />
                                            </button>
                                        )}
                                        <button
                                            className="support-trump-card-close-button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (isFlipped) {
                                                    setIsFlipped(false);
                                                    setTimeout(() => {
                                                        onCloseActive();
                                                    }, 300);
                                                } else {
                                                    onCloseActive();
                                                }
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.transform = 'scale(1.15)';
                                                e.currentTarget.style.opacity = '1';
                                                e.currentTarget.style.background = 'rgba(0,0,0,0.25)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.transform = 'scale(1)';
                                                e.currentTarget.style.opacity = '0.85';
                                                e.currentTarget.style.background = 'rgba(0,0,0,0.15)';
                                            }}
                                            onMouseDown={(e) => {
                                                e.currentTarget.style.transform = 'scale(0.95)';
                                            }}
                                            onMouseUp={(e) => {
                                                e.currentTarget.style.transform = 'scale(1.15)';
                                            }}
                                            aria-label="Close"
                                        >
                                            ×
                                        </button>
                                    </>
                                ) : null}
                            </div>

                            {/* Overall Score */}
                            <div className="support-trump-card-front-overall-score">
                                <div>Trending Score</div>
                                <div style={{
                                    color: metrics.overallScore >= topPerformerScore ? '#2e7d32' : metrics.overallScore < bottomPerformerScore ? '#c62828' : '#1976d2',
                                    fontSize: '18px'
                                }}>
                                    {metrics.overallScore.toFixed(1)}%
                                </div>
                            </div>

                            {/* Metrics */}
                            <div className="support-trump-card-front-metrics">                                
                                <MetricRow
                                    label="Resolution Rate"
                                    value={metrics.resolutionRateTrend}
                                    inverse={false}
                                    subValue={metrics.resolutionRateDifferenceToPreviousMonth !== 0 ? `(${metrics.resolutionRateDifferenceToPreviousMonth > 0 ? '+' : '-'}${Math.abs(metrics.resolutionRateDifferenceToPreviousMonth).toFixed(1)}%)` : '(no change)'}

                                />
                                <MetricRow
                                    label="Resolution Time"
                                    value={metrics.resolutionTimeTrend}
                                    inverse={true}
                                    subValue={metrics.resolutionTimeDifferenceToPreviousMonth !== 0 ? `(${metrics.resolutionTimeDifferenceToPreviousMonth > 0 ? '+' : '-'}${formatTime(Math.abs(metrics.resolutionTimeDifferenceToPreviousMonth))})` : '(no change)'}
                                />
                                <MetricRow
                                    label="Response Time"
                                    value={metrics.firstResponseTimeTrend}
                                    inverse={true}
                                    subValue={metrics.firstResponseTimeDifferenceToPreviousMonth !== 0 ? `(${metrics.firstResponseTimeDifferenceToPreviousMonth > 0 ? '+' : '-'}${formatTime(Math.abs(metrics.firstResponseTimeDifferenceToPreviousMonth))})` : '(no change)'}
                                />
                                <MetricRow
                                    label="Resolved Cases"
                                    value={metrics.casesResolvedTrend}
                                    inverse={false}
                                    subValue={metrics.casesResolvedDifferenceToPreviousMonth !== 0 ? `(${metrics.casesResolvedDifferenceToPreviousMonth > 0 ? '+' : '-'}${Math.abs(metrics.casesResolvedDifferenceToPreviousMonth)} case${metrics.casesResolvedDifferenceToPreviousMonth > 1 || metrics.casesResolvedDifferenceToPreviousMonth < -1 ? 's' : ''})` : '(no change)'}
                                />
                            </div>

                            <div className="support-trump-card-footer">
                                Click to see explanation
                            </div>
                        </div>

                        {/* Back of card with statistics explanations */}
                        <div className="support-trump-card-back" style={{
                            background: cardColor,
                            border: `2px solid ${borderColor}`
                        }}>
                            {/* Card Header */}
                            <div className="support-trump-card-header" style={{ background: headerColor }}>
                                <div className="support-trump-card-header-content">
                                    <h3 className="support-trump-card-header-title">{memberName}</h3>
                                    {metrics.isNewStaff ? (
                                        <div className="support-trump-card-new-badge" style={{ color: headerColor }}>NEW</div>
                                    ) : (
                                        <div className="support-trump-card-placeholder"></div>
                                    )}
                                </div>
                                {active && onCloseActive && (
                                    <button
                                        className="support-trump-card-close-button"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (isFlipped) {
                                                setIsFlipped(false);
                                                setTimeout(() => {
                                                    onCloseActive();
                                                }, 300);
                                            } else {
                                                onCloseActive();
                                            }
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.transform = 'scale(1.15)';
                                            e.currentTarget.style.opacity = '1';
                                            e.currentTarget.style.background = 'rgba(0,0,0,0.25)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = 'scale(1)';
                                            e.currentTarget.style.opacity = '0.85';
                                            e.currentTarget.style.background = 'rgba(0,0,0,0.15)';
                                        }}
                                        onMouseDown={(e) => {
                                            e.currentTarget.style.transform = 'scale(0.95)';
                                        }}
                                        onMouseUp={(e) => {
                                            e.currentTarget.style.transform = 'scale(1.15)';
                                        }}
                                        aria-label="Close"
                                    >
                                        ×
                                    </button>
                                )}
                            </div>

                            {/* Explanation of each metric */}
                            <div className="support-trump-card-back-metrics">
                                <div className="support-trump-card-back-metrics-content">

                                    {/* Resolution Rate */}
                                    <div className="support-trump-card-back-metrics-explanation">
                                        <h3>Resolution Rate</h3>
                                        <p><strong>What it is:</strong> Resolution Rate measures how effectively a support team member closes the cases assigned to them. It's the percentage of assigned cases that were successfully resolved.</p>
                                        <p><strong>Why it matters:</strong> A high resolution rate indicates strong problem-solving skills and the ability to help customers effectively. It shows reliability in handling customer issues.</p>
                                        <p><strong>How we calculate it:</strong> (Resolved Cases / Assigned Cases) × 100. If no cases were assigned but some were resolved, we use 100%. The trend compares the percentage change between current and previous months.</p>
                                    </div>

                                    {/* Resolution Time */}
                                    <div className="support-trump-card-back-metrics-explanation">
                                        <h3>Resolution Time</h3>
                                        <p><strong>What it is:</strong> Resolution Time is the average time it takes to fully resolve a customer case from when it was first assigned to when it's marked as resolved.</p>
                                        <p><strong>Why it matters:</strong> Faster resolution times mean happier customers and more efficient support operations. However, quality shouldn't be sacrificed for speed.</p>
                                        <p><strong>How we calculate it:</strong> We average the resolution time in minutes for all resolved cases. The trend is calculated as the percentage change from the previous month, with negative trends (decreasing time) considered positive improvements.</p>
                                    </div>

                                    {/* Response Time */}
                                    <div className="support-trump-card-back-metrics-explanation">
                                        <h3>Response Time</h3>
                                        <p><strong>What it is:</strong> Response Time measures how quickly a support team member provides their first meaningful response to a new case assignment.</p>
                                        <p><strong>Why it matters:</strong> Quick first responses show customers that their issues are being taken seriously and help set proper expectations for resolution.</p>
                                        <p><strong>How we calculate it:</strong> We average the first response time in minutes for all cases. The trend is calculated as the percentage change from the previous month, with negative trends (decreasing time) considered positive improvements.</p>
                                    </div>

                                    {/* Case Volume */}
                                    <div className="support-trump-card-back-metrics-explanation">
                                        <h3>Case Volume</h3>
                                        <p><strong>What it is:</strong> Case Volume tracks the number of cases resolved by a team member in a given period, showing their productivity.</p>
                                        <p><strong>Why it matters:</strong> Understanding case volume helps identify high-performing team members and assess overall workload distribution.</p>
                                        <p><strong>How we calculate it:</strong> Total number of cases resolved in the current month. The trend is calculated as the percentage change in resolved cases compared to the previous month.</p>
                                    </div>

                                    {/* Overall Score */}
                                    <div className="support-trump-card-back-metrics-explanation">
                                        <h3>Overall Score</h3>
                                        <p><strong>What it is:</strong> A comprehensive performance score that combines current performance and trend metrics into a single rating.</p>
                                        <p><strong>How we calculate it:</strong></p>
                                        <ul>
                                            <li>Current Resolution Rate (40%): Percentage of assigned cases resolved</li>
                                            <li>Resolution Productivity (30%): Percentage of total team resolutions contributed by this member</li>
                                            <li>Trend Score (30%): Combined improvement trends weighted as follows:</li>
                                            <ul>
                                                <li>Resolution Rate Trend (40%)</li>
                                                <li>Resolution Time Trend (30%)</li>
                                                <li>Response Time Trend (20%)</li>
                                                <li>Cases Resolved Trend (10%)</li>
                                            </ul>
                                        </ul>
                                    </div>

                                </div>
                            </div>

                            <div className="support-trump-card-footer">
                                Click to flip back
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default SupportMemberTrumpCard; 